/**
 * 一覧画面。
 *
 * - フィルタタブ（今日 / 今週 / 全件 / キャンセル）
 * - 件数カウント・空状態
 * - 予約カード（顧客名・日時・連絡方法・ステータスバッジ）
 * - カードタップ → 詳細画面遷移
 *
 * 画面遷移時は state を更新してから renderList() / renderDetail() を呼び直す。
 * イベントは event delegation 方式（一覧 div への click 一本で済ませる）。
 */

import { listReservations } from "../api";
import { setRoot, escapeHtml, showError } from "../render";
import { state } from "../store";
import type { Filter, Reservation, ReservationStatus } from "../types";
import { renderDetail } from "./detail";

const FILTER_LABELS: Record<Filter, string> = {
  today: "今日",
  week: "今週",
  all: "全件",
  canceled: "キャンセル",
};

const STATUS_LABELS: Record<ReservationStatus, string> = {
  confirmed: "確定",
  completed: "面談済",
  no_show: "フォロー",
  canceled: "キャンセル",
};

const CONTACT_LABELS: Record<string, string> = {
  zoom: "オンライン面談",
  line_call: "LINE通話",
};

/**
 * 今日の予約カードを薄い黄色背景でハイライトするためのチェック関数。
 * Asia/Tokyo タイムゾーン基準で「今日（YYYY-MM-DD）」を算出。
 * sv-SE ロケールが ISO 形式（YYYY-MM-DD）を返す性質を活用。
 */
function isTodayJst(reservationDate: string): boolean {
  const todayStr = new Date().toLocaleDateString("sv-SE", { timeZone: "Asia/Tokyo" });
  return reservationDate === todayStr;
}

export async function renderList(): Promise<void> {
  state.view = "list";

  // ローディング表示
  setRoot(loadingShell());

  try {
    const result = await listReservations(state.filter);
    state.reservations = result.reservations;
    paint();
  } catch (e: any) {
    showError(`一覧の取得に失敗しました：${e?.message ?? e}`);
    setRoot(errorShell());
    bindFilterTabs();
  }
}

function loadingShell(): string {
  return `
    ${headerHtml()}
    ${filterTabsHtml()}
    <div class="text-center text-gray-500 mt-8">読み込み中...</div>
  `;
}

function errorShell(): string {
  return `
    ${headerHtml()}
    ${filterTabsHtml()}
    <div class="text-center text-red-600 mt-8 px-4">
      予約の取得に失敗しました。<br>
      通信状況を確認して、もう一度お試しください。
    </div>
    <div class="mt-6 text-center">
      <button id="btn-retry" class="tap-target px-6 py-3 bg-brand-green text-white rounded-lg">
        再読み込み
      </button>
    </div>
  `;
}

function paint(): void {
  setRoot(`
    ${headerHtml()}
    ${filterTabsHtml()}
    ${countHtml(state.reservations.length)}
    ${cardsHtml(state.reservations)}
  `);
  bindFilterTabs();
  bindCards();
}

function headerHtml(): string {
  return `
    <header class="mb-3 pb-2 border-b flex items-center justify-between">
      <div>
        <h1 class="text-xl font-bold text-gray-900">予約管理</h1>
        <p class="text-xs text-gray-500 mt-1">Empath LINE オーナー専用</p>
      </div>
      <button id="btn-go-settings"
        class="text-base font-semibold bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white px-4 py-2.5 rounded-lg shadow-sm flex-shrink-0"
        aria-label="営業日設定">
        ⚙ 設定
      </button>
    </header>
  `;
}

function filterTabsHtml(): string {
  const tabs: Filter[] = ["today", "week", "all", "canceled"];
  return `
    <div id="filter-tabs" class="flex gap-2 mb-3 overflow-x-auto" role="tablist">
      ${tabs.map((f) => {
        const active = f === state.filter;
        return `
          <button
            data-filter="${f}"
            role="tab"
            aria-selected="${active}"
            class="tap-target px-4 py-2 rounded-full text-sm whitespace-nowrap ${
              active
                ? "bg-brand-green text-white"
                : "bg-white border border-gray-300 text-gray-700"
            }">
            ${escapeHtml(FILTER_LABELS[f])}
          </button>
        `;
      }).join("")}
    </div>
  `;
}

function countHtml(n: number): string {
  return `<div class="text-sm text-gray-600 mb-2">${n} 件</div>`;
}

function cardsHtml(rs: Reservation[]): string {
  if (rs.length === 0) {
    return `
      <div class="bg-white rounded-lg border border-gray-200 p-6 text-center text-gray-500 mt-2">
        該当する予約はありません。
      </div>
    `;
  }
  return `
    <div id="cards" class="flex flex-col gap-2">
      ${rs.map((r) => cardHtml(r)).join("")}
    </div>
  `;
}

function cardHtml(r: Reservation): string {
  const dateLabel = formatDate(r.reservation_date);
  // 今日（JST）の予定は薄い黄色で差別化
  const isToday = isTodayJst(r.reservation_date);
  const isCompleted = r.status === "completed";
  // 優先順位：面談済 > 本日 > 通常（終わった予定は「今日」より「完了」を強調）
  const cardBg = isCompleted
    ? "bg-blue-50 border-blue-200 hover:bg-blue-100 active:bg-blue-200"
    : isToday
    ? "bg-yellow-50 border-yellow-300 hover:bg-yellow-100 active:bg-yellow-200"
    : "bg-white border-gray-200 hover:bg-gray-50 active:bg-gray-100";
  const todayBadge = isToday && !isCompleted
    ? `<div class="text-xs font-bold text-yellow-700 mb-1">📅 本日</div>`
    : "";
  return `
    <button
      data-id="${escapeHtml(r.id)}"
      class="tap-target text-left rounded-lg border p-3 w-full ${cardBg}">
      ${todayBadge}
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          <div class="font-semibold text-gray-900 truncate">${escapeHtml(r.customer_name)}</div>
          <div class="text-sm text-gray-700 mt-0.5">
            ${escapeHtml(dateLabel)} ${escapeHtml(r.reservation_time)}
            ・${escapeHtml(CONTACT_LABELS[r.contact_method] ?? r.contact_method)}
          </div>
        </div>
        <span class="badge badge-${r.status} flex-shrink-0">
          ${escapeHtml(STATUS_LABELS[r.status])}
        </span>
      </div>
    </button>
  `;
}

function bindFilterTabs(): void {
  const container = document.getElementById("filter-tabs");
  if (!container) return;
  container.addEventListener("click", (ev) => {
    const target = (ev.target as HTMLElement).closest("[data-filter]");
    if (!target) return;
    const f = target.getAttribute("data-filter") as Filter;
    if (f && f !== state.filter) {
      state.filter = f;
      renderList();
    }
  });
  // 再読み込みボタン
  const retry = document.getElementById("btn-retry");
  if (retry) {
    retry.addEventListener("click", () => renderList());
  }
  // 営業日設定画面への遷移ボタン
  const settingsBtn = document.getElementById("btn-go-settings");
  if (settingsBtn) {
    settingsBtn.addEventListener("click", async () => {
      const { renderBusinessSettings } = await import("./businessSettings");
      await renderBusinessSettings();
    });
  }
}

function bindCards(): void {
  const container = document.getElementById("cards");
  if (!container) return;
  container.addEventListener("click", (ev) => {
    const target = (ev.target as HTMLElement).closest("[data-id]") as HTMLElement | null;
    if (!target) return;
    const id = target.getAttribute("data-id");
    if (!id) return;
    const r = state.reservations.find((x) => x.id === id) ?? null;
    state.selected = r;
    renderDetail(id);
  });
}

/**
 * YYYY-MM-DD → "M/D(曜)" 表示に整形。
 */
function formatDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const [, y, mo, d] = m;
  const dt = new Date(Number(y), Number(mo) - 1, Number(d));
  const w = ["日", "月", "火", "水", "木", "金", "土"][dt.getDay()];
  return `${Number(mo)}/${Number(d)}(${w})`;
}
